import React, { useEffect, useState, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../services/apiService";
import axios from "axios";
import AIDisclaimer from "../components/AIDisclaimer";
import './css/LessonResourceDetail.css';

function LessonResourceDetail() {
  const { lessonId, resourceId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [primaryResource, setPrimaryResource] = useState(null);
  const [summaryText, setSummaryText] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("transcribe");
  const [summarizing, setSummarizing] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptResource, setTranscriptResource] = useState(null);
  const [transcriptText, setTranscriptText] = useState("");
  const [summaryResource, setSummaryResource] = useState(null);
  const [summaryPollingDocId, setSummaryPollingDocId] = useState(null);
  const [isEditingTranscript, setIsEditingTranscript] = useState(false);
  const [editedTranscript, setEditedTranscript] = useState("");
  const [isEditingSummary, setIsEditingSummary] = useState(false);
  const [editedSummary, setEditedSummary] = useState("");
  const [savingTranscript, setSavingTranscript] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);
  const [showTranscriptMenu, setShowTranscriptMenu] = useState(false);
  const [showSummaryMenu, setShowSummaryMenu] = useState(false);
  
  // Toggle between Video Indexer (old) and Speech Services (new)
  const usingSpeechServices = true;
  
  // Use ref to store interval IDs to clear them properly
  const summaryIntervalRef = useRef(null);
  const isPollingRef = useRef(false);
  const transcriptMenuRef = useRef(null);
  const summaryMenuRef = useRef(null);

  // Handle click outside to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (transcriptMenuRef.current && !transcriptMenuRef.current.contains(event.target)) {
        setShowTranscriptMenu(false);
        setShowSummaryMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to process lesson data and set related states
  const processLessonData = async (lessonData) => {
    setLesson(lessonData);

    // Primary resource id is taken from URL
    const mainResource = lessonData.doc_resources.find(r => r.id.toString() === resourceId);
    setPrimaryResource(mainResource);

    if (!mainResource) return;

    // Find and set summary resource and text
    const associatedSummary = lessonData.doc_resources.find(
      r => r.serialNumber === mainResource.serialNumber && r.category === 'summary'
    );
    if (associatedSummary) {
      setSummaryResource(associatedSummary);
      if (associatedSummary.filetype === 'txt' && associatedSummary.resource_url) {
        try {
          const response = await axios.get(associatedSummary.resource_url);
          setSummaryText(response.data || "");
        } catch (fetchError) {
          console.error("Failed to fetch summary text:", fetchError);
          setSummaryText("Could not load summary.");
        }
      }
    } else {
      setSummaryResource(null);
    }

    const associatedTranscript = lessonData.doc_resources.find(
      r => r.serialNumber === mainResource.serialNumber && r.category === 'transcript'
    );

    // Find and set transcript resource and text for videos
    if (mainResource.filetype === 'video') {
      if (associatedTranscript) {
        setTranscriptResource(associatedTranscript);
        if (associatedTranscript.filetype === 'txt' && associatedTranscript.resource_url) {
          try {
            const response = await axios.get(associatedTranscript.resource_url);
            setTranscriptText(response.data || "");
          } catch (fetchError) {
            console.error("Failed to fetch transcript text:", fetchError);
            setTranscriptText("");
          }
        }
      }
    }

    // Set the initial active tab based on content type
    const contentType = mainResource?.filetype;
    if (contentType === "video") {
      setActiveTab("transcribe");
    } else {
      setActiveTab("summary");
    }

    // Check if summary is in progress and resume polling if needed
    // For videos: only check for summary if transcript exists (summary is generated from transcript)
    // For PDF/text files: check the primary resource's summary
    let resourceToCheckForSummary = null;
    
    if (mainResource.filetype === 'video') {
      // For videos, only check summary on the transcript resource (if it exists)
      if (associatedTranscript) {
        resourceToCheckForSummary = associatedTranscript;
      }
      // If no transcript exists yet, don't check for summary at all
    } else {
      // For non-video resources (PDF, text), check the main resource
      resourceToCheckForSummary = mainResource;
    }

    // Check if summary generation should be resumed (only if no summary exists yet)
    if (resourceToCheckForSummary && !associatedSummary?.resource_url) {
      const jobStatus = resourceToCheckForSummary.job_status;
      
      // If resource itself shows processing/pending status, start polling immediately
      if (jobStatus === 'processing' || jobStatus === 'pending') {
        console.log("Resource is being processed, resuming summary polling for document:", resourceToCheckForSummary.id);
        setSummaryPollingDocId(resourceToCheckForSummary.id);
        setSummarizing(true);
      } else {
        // Otherwise, check via API if summary generation is in progress
        try {
          const statusResponse = await apiService.checkSummarizeStatus(lessonId, resourceToCheckForSummary.id);
          if (statusResponse.success && statusResponse.data.status === 'processing') {
            console.log("Resuming summary polling for document:", resourceToCheckForSummary.id);
            setSummaryPollingDocId(resourceToCheckForSummary.id);
            setSummarizing(true);
          }
        } catch (err) {
          console.log("No summary in progress or failed to check status");
        }
      }
    }
  };

  useEffect(() => {
    async function fetchLessonAndResource() {
      setLoading(true);
      setError(null);
      try {
        const lessonData = await apiService.getLessonById(lessonId);
        await processLessonData(lessonData);
      } catch (err) {
        setError("Failed to load lesson.");
      } finally {
        setLoading(false);
      }
    }
    fetchLessonAndResource();
  }, [lessonId, resourceId]);

  useEffect(() => {
    // Check for transcription in progress based on service type
    const isTranscriptionInProgress = usingSpeechServices
      ? (primaryResource && (primaryResource.job_status === 'processing' || primaryResource.job_status === 'in-progress') && primaryResource.speech_transcription_id)
      : (primaryResource && primaryResource.job_status === 'in-progress' && primaryResource.video_index);

    if (isTranscriptionInProgress) {
      console.log("✅ Starting transcription polling interval");
      const interval = setInterval(async () => {
        try {
          
          let response;
          if (usingSpeechServices) {
            // Use Speech Services API - simpler, just needs document_id
            response = await apiService.checkSpeechTranscriptionJobStatus(
              lessonId,
              primaryResource.id
            );
          } else {
            // Use Video Indexer API - needs video_index and access_token
            response = await apiService.checkTranscriptionJobStatus(
              lessonId,
              primaryResource.video_index,
              primaryResource.video_access_token,
              primaryResource.id
            );
          }
          
          if (response.success && response.data?.job_status === 'completed') {
            toast.success("Transcription is complete!");
            clearInterval(interval);
            const lessonData = await apiService.getLessonById(lessonId);
            await processLessonData(lessonData);
          } else if (response.data?.job_status === 'failed') {
            toast.error("Transcription job failed. Please try again.");
            console.error("Transcription job failed:", response.error);
            clearInterval(interval);
            const lessonData = await apiService.getLessonById(lessonId);
            setLesson(lessonData);
          } else if (!response.success) {
            console.error("Failed to check transcription status:", response.error);
            clearInterval(interval);
          }
        } catch (err) {
          console.error("Error polling for transcription status:", err);
          clearInterval(interval);
        }
      }, 10000); // Poll every 10 seconds

      return () => clearInterval(interval);
    }
  }, [lessonId, primaryResource, usingSpeechServices]);

  // New polling effect for summary status using the new API
  useEffect(() => {
    if (summaryPollingDocId) {
      console.log("Starting summary status polling for document ID:", summaryPollingDocId);
      
      const pollSummaryStatus = async () => {
        // Prevent overlapping requests
        if (isPollingRef.current) {
          console.log("Previous poll still in progress, skipping...");
          return;
        }

        isPollingRef.current = true;
        try {
          console.log("Checking summary status...");
          const response = await apiService.checkSummarizeStatus(lessonId, summaryPollingDocId);

          if (response.success) {
            const { status, summary, summary_url, error } = response.data;

            if (status === 'completed') {
              // Clear polling first to prevent duplicate calls
              if (summaryIntervalRef.current) {
                clearInterval(summaryIntervalRef.current);
                summaryIntervalRef.current = null;
              }
              setSummaryPollingDocId(null);
              isPollingRef.current = false;

              // Fetch and display the summary
              if (summary_url) {
                try {
                  const summaryResponse = await axios.get(summary_url);
                  setSummaryText(summaryResponse.data || "");
                  setSummarizing(false);
                  toast.success("Summary is complete!");
                } catch (fetchError) {
                  console.error("Failed to fetch summary from URL:", fetchError);
                  setSummaryText("Summary completed but could not load content.");
                  setSummarizing(false);
                  toast.warning("Summary completed but could not load content.");
                }
              } else if (summary) {
                setSummaryText(summary);
                setSummarizing(false);
                toast.success("Summary is complete!");
              } else {
                setSummarizing(false);
              }

            } else if (status === 'failed') {
              console.error("❌ Summary generation failed:", error);
              
              // Clear polling
              if (summaryIntervalRef.current) {
                clearInterval(summaryIntervalRef.current);
                summaryIntervalRef.current = null;
              }
              setSummaryPollingDocId(null);
              isPollingRef.current = false;
              setSummarizing(false);
              
              toast.error(`Summary generation failed: ${error || 'Unknown error'}`);

            } else if (status === 'processing') {
              console.log("Summary still processing...");
              isPollingRef.current = false;
            }
          } else {
            console.error("Failed to check summary status:", response.error);
            isPollingRef.current = false;
          }
        } catch (err) {
          console.error("Error polling for summary status:", err);
          isPollingRef.current = false;
        }
      };

      // Start polling immediately
      pollSummaryStatus();
      
      // Then poll every 5 seconds
      summaryIntervalRef.current = setInterval(pollSummaryStatus, 5000);

      // Cleanup function
      return () => {
        if (summaryIntervalRef.current) {
          clearInterval(summaryIntervalRef.current);
          summaryIntervalRef.current = null;
        }
        isPollingRef.current = false;
      };
    }
  }, [lessonId, summaryPollingDocId]);

  // --- Render Functions & Sub-Components ---

  const PdfViewer = ({ url, filename }) => {
    const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(url)}&embedded=true`;
    return (
      <div className="document-viewer-container">
        <div className="iframe-container">
          <iframe className="resource-iframe" src={googleDocsViewerUrl} title={filename} width="100%" height="750px" />
        </div>
      </div>
    );
  };

  const VideoPlayer = ({ url, filename }) => {
    if (url && (url.includes("youtube.com") || url.includes("youtu.be"))) {
      let videoId = "";
      if (url.includes("youtube.com")) {
        const urlParams = new URLSearchParams(url.split('?')[1]);
        videoId = urlParams.get("v");
      } else if (url.includes("youtu.be")) {
        videoId = url.split("/").pop().split("?")[0];
      }
      if (videoId) {
        const embedUrl = `https://www.youtube.com/embed/${videoId}`;
        return <iframe className="resource-video-embed" src={embedUrl} title={filename} width="100%" height="400" allowFullScreen />;
      }
    }
    return <video className="resource-video" src={url} controls width="100%" />;
  };

  const ResourceContentPanel = ({ resource }) => {
    if (!resource) return <p>Resource not found.</p>;
    const contentType = resource.filetype?.toLowerCase();

    switch (contentType) {
      case 'pdf':
      case 'text':
      case 'document':
        return <PdfViewer url={resource.resource_url} filename={resource.filename} />;
      case 'video':
        return <VideoPlayer url={resource.resource_url} filename={resource.filename} />;
      default:
        return (
          <div className="unsupported-content-container">
            <p>Unable to display this content type.</p>
            <a className="btn btn-primary" href={resource.resource_url} target="_blank" rel="noopener noreferrer">
              Open in New Tab
            </a>
          </div>
        );
    }
  };

  const TranscriptPanel = () => {
    if (transcriptText) {
      return (
        <>
          <AIDisclaimer />
          {isEditingTranscript ? (
            <div>
              <textarea
                className="edit-textarea"
                value={editedTranscript}
                onChange={(e) => setEditedTranscript(e.target.value)}
                rows={15}
              />
              <div className="edit-actions">
                <button 
                  className="btn btn-primary" 
                  onClick={handleSaveTranscript} 
                  disabled={savingTranscript}
                >
                  {savingTranscript ? 'Saving...' : 'Save'}
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={handleCancelEditTranscript}
                  disabled={savingTranscript}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              <p className="transcript-container">{transcriptText}</p>
            </>
          )}
        </>
      );
    }

    // Check if transcription is in progress based on service type
    const isInProgress = usingSpeechServices
      ? (primaryResource && primaryResource.speech_transcription_id && (primaryResource.job_status === 'processing' || primaryResource.job_status === 'in-progress'))
      : (primaryResource && primaryResource.video_index && primaryResource.job_status === 'in-progress');

    if (isInProgress) {
      return (
        <div className="transcription-progress-notice">
          Transcription is in progress, please come back after some time.
        </div>
      );
    }

    return (
      <button className="btn btn-primary resource-action-btn" onClick={handleTranscribeVideo} disabled={isTranscribing || summarizing}>
        {isTranscribing ? "Transcribing..." : "Create Transcription"}
      </button>
    );
  };

  const renderContent = () => {
    if (!primaryResource) return <p>Resource not found.</p>;
    const contentType = primaryResource.filetype?.toLowerCase();

    switch (contentType) {
      case 'pdf':
      case 'text':
      case 'document':
        return <PdfViewer url={primaryResource.resource_url} filename={primaryResource.filename} />;
      case 'video':
        return <VideoPlayer url={primaryResource.resource_url} filename={primaryResource.filename} />;
      default:
        return (
          <div className="unsupported-content-container">
            <p>Unable to display this content type.</p>
            <a className="btn btn-primary" href={primaryResource.resource_url} target="_blank" rel="noopener noreferrer">
              Open in New Tab
            </a>
          </div>
        );
    }
  };

  const renderLeftPanel = () => {
    const contentType = primaryResource?.filetype?.toLowerCase();
    
    // Determine if menu should be shown based on file type and available content
    const showMenu = (contentType === "video" && (transcriptText || summaryText)) ||
                     (contentType !== "video" && summaryText);
    
    // Use a single menu state
    const isMenuOpen = showTranscriptMenu || showSummaryMenu;
    
    return (
      <>
        <div className="course-detail-tabs">
          <div className="tabs-wrapper">
            {contentType === "video" && (
              <div 
                className={`course-detail-tab ${activeTab === "transcribe" ? "active" : ""}`}
                onClick={() => setActiveTab("transcribe")}
              >
                Transcript
              </div>
            )}
            <div 
              className={`course-detail-tab ${activeTab === "summary" ? "active" : ""}`}
              onClick={() => setActiveTab("summary")}
            >
              Summary
            </div>
          </div>
          {showMenu && (
            <div className="menu-container" ref={transcriptMenuRef}>
              <button 
                className="menu-button" 
                onClick={() => {
                  setShowTranscriptMenu(!isMenuOpen);
                  setShowSummaryMenu(!isMenuOpen);
                }}
                aria-label="Options"
              >
                ⋮
              </button>
              {isMenuOpen && (
                <div className="menu-dropdown">
                  {contentType === "video" && transcriptText && (
                    <button 
                      className="menu-item" 
                      onClick={() => {
                        setActiveTab("transcribe");
                        setTimeout(() => handleEditTranscript(), 0);
                        setShowTranscriptMenu(false);
                        setShowSummaryMenu(false);
                      }}
                    >
                      ✏️ Edit Transcript
                    </button>
                  )}
                  {summaryText && (
                    <button 
                      className="menu-item" 
                      onClick={() => {
                        setActiveTab("summary");
                        setTimeout(() => handleEditSummary(), 0);
                        setShowTranscriptMenu(false);
                        setShowSummaryMenu(false);
                      }}
                    >
                      ✏️ Edit Summary
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="resource-tab-content">
          {activeTab === "transcribe" && contentType === "video" && (
            <div>
              <TranscriptPanel />
            </div>
          )}
          {activeTab === "summary" && (
            <div>
              {(() => {
                if (summaryText) {
                  return (
                    <>
                      <AIDisclaimer message="This summary was generated by AI. Please verify accuracy before using it." />
                      {isEditingSummary ? (
                        <div>
                          <textarea
                            className="edit-textarea"
                            value={editedSummary}
                            onChange={(e) => setEditedSummary(e.target.value)}
                            rows={15}
                          />
                          <div className="edit-actions">
                            <button 
                              className="btn btn-primary" 
                              onClick={handleSaveSummary} 
                              disabled={savingSummary}
                            >
                              {savingSummary ? 'Saving...' : 'Save'}
                            </button>
                            <button 
                              className="btn btn-secondary" 
                              onClick={handleCancelEditSummary}
                              disabled={savingSummary}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p>{summaryText}</p>
                        </>
                      )}
                    </>
                  );
                }
                if (summarizing || summaryPollingDocId) {
                  return (
                    <div className="transcription-progress-notice">
                      Summary generation is in progress, please wait...
                    </div>
                  );
                }
                
                // For videos, check if transcript exists before allowing summary
                if (contentType === "video" && !transcriptText) {
                  return (
                    <div className="transcription-progress-notice">
                      You need to create a transcription first before generating a summary. Please go to the Transcription tab.
                    </div>
                  );
                }
                
                return (
                  <button className="btn btn-primary resource-action-btn" onClick={handleCreateSummary} disabled={summarizing || isTranscribing}>
                    {summarizing ? "Starting..." : "Create Summary"}
                  </button>
                );
              })()}
            </div>
          )}
        </div>
      </>
    );
  };

  // --- Event Handlers ---

  const handleDownload = async (url, filename) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Error downloading file:', err);
      toast.error('Failed to download file');
    }
  };

  const handleCreateSummary = async (e) => {
    e.preventDefault();

    if (!lessonId) {
      toast.error('Missing lesson ID');
      return;
    }

    if (!primaryResource?.id) {
      toast.error('Missing resource ID to create summary.');
      return;
    }

    try {
      setSummarizing(true);

      const resourceIdForSummary =
        primaryResource.filetype === 'video' && transcriptResource
          ? transcriptResource.id
          : primaryResource.id;
      
      const response = await apiService.summarizeLesson(lessonId, resourceIdForSummary);
      
      if (response.success) {
        toast.info('Summary generation started. Please wait...');
        
        // Start polling for status with the document ID
        setSummaryPollingDocId(resourceIdForSummary);
        
      } else {
        toast.error(response.error || 'Failed to start summary generation');
        setSummarizing(false);
      }

    } catch (err) {
      console.error('Error creating summary:', err);
      toast.error(err.message || 'Failed to create summary');
      setSummarizing(false);
    }
  };

  const handleTranscribeVideo = async (e) => {
    e.preventDefault();

    if (!lessonId) {
      toast.error('Missing lesson ID');
      return;
    }

    if (primaryResource?.filetype !== 'video' || !primaryResource?.id) {
      toast.error('Transcription is only available for saved video resources.');
      return;
    }

    try {
      setIsTranscribing(true);
      
      let response;
      if (usingSpeechServices) {
        // Use Speech Services API
        response = await apiService.startSpeechTranscriptionJob(parseInt(lessonId), primaryResource.id);
      } else {
        // Use Video Indexer API
        response = await apiService.startTranscriptionJob(parseInt(lessonId), primaryResource.id);
      }

      if (response.success) {
        console.log("Transcription started, response:", response.data);
        toast.success('Transcription job started successfully. The transcript will be available once processing is complete.');
        const lessonData = await apiService.getLessonById(lessonId);
        setLesson(lessonData);
        const updatedResource = lessonData.doc_resources.find(r => r.id.toString() === resourceId);
        console.log("Updated resource after transcription start:", updatedResource);
        setPrimaryResource(updatedResource);
      } else {
        toast.error(response.error || 'Failed to start transcription job');
      }
    } catch (err) {
      console.error('Error starting transcription:', err);
      toast.error(err.message || 'Failed to start transcription job');
    } finally {
      setIsTranscribing(false);
    }
  };

  const handleEditTranscript = () => {
    setEditedTranscript(transcriptText);
    setIsEditingTranscript(true);
  };

  const handleCancelEditTranscript = () => {
    setIsEditingTranscript(false);
    setEditedTranscript("");
  };

  const handleSaveTranscript = async () => {
    if (!transcriptResource?.id) {
      toast.error('Transcript resource not found');
      return;
    }

    try {
      setSavingTranscript(true);
      const response = await apiService.editTranscription(
        lessonId,
        transcriptResource.id,
        editedTranscript
      );

      if (response.success) {
        setTranscriptText(editedTranscript);
        setIsEditingTranscript(false);
        toast.success('Transcript updated successfully');
      } else {
        toast.error(response.error || 'Failed to update transcript');
      }
    } catch (err) {
      console.error('Error saving transcript:', err);
      toast.error(err.message || 'Failed to save transcript');
    } finally {
      setSavingTranscript(false);
    }
  };

  const handleEditSummary = () => {
    setEditedSummary(summaryText);
    setIsEditingSummary(true);
  };

  const handleCancelEditSummary = () => {
    setIsEditingSummary(false);
    setEditedSummary("");
  };

  const handleSaveSummary = async () => {
    if (!summaryResource?.id) {
      toast.error('Summary resource not found');
      return;
    }

    try {
      setSavingSummary(true);
      const response = await apiService.editSummarization(
        lessonId,
        summaryResource.id,
        editedSummary
      );

      if (response.success) {
        setSummaryText(editedSummary);
        setIsEditingSummary(false);
        toast.success('Summary updated successfully');
      } else {
        toast.error(response.error || 'Failed to update summary');
      }
    } catch (err) {
      console.error('Error saving summary:', err);
      toast.error(err.message || 'Failed to save summary');
    } finally {
      setSavingSummary(false);
    }
  };

  // Memoize the right panel content to prevent unnecessary re-renders
  const rightPanelContent = useMemo(() => {
    return <ResourceContentPanel resource={primaryResource} />;
  }, [primaryResource?.id, primaryResource?.resource_url]);

  if (loading) return <div>Loading lesson...</div>;
  if (error) return <div>{error}</div>;
  if (!lesson) return <div>No lesson found.</div>;

  return (
    <div>
      <button className="btn btn-secondary"
        onClick={() => navigate(`/lessons/${lessonId}/resources`)}>
        ← Back
      </button>
      <h2 className="resource-detail-header">{lesson.title}</h2>
      <div className="resource-filename" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Viewing resource: <strong>{primaryResource?.filename}</strong></span>
        {(primaryResource?.filetype === 'pdf' || primaryResource?.filetype === 'file') && primaryResource?.resource_url && (
          <button 
            className="btn btn-primary" 
            onClick={() => handleDownload(primaryResource.resource_url, primaryResource.filename)}
            style={{ marginLeft: '1rem' }}
          >
            📥 Download PDF
          </button>
        )}
      </div>
      <div className="resource-detail-container">
        <div className="resource-left-panel">
          {renderLeftPanel()}
        </div>
        <div className="resource-right-panel">
          {rightPanelContent}
        </div>
      </div>
    </div>
  );
}

export default LessonResourceDetail;